import React, { useCallback, useEffect, useMemo } from 'react'

import { Path } from 'constants/router'
import { StatusPage } from 'components/common/StatusPage'
import { useAuth0 } from 'utils/auth'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useVerifyEmail } from 'dataQueries'

/**
 * @component
 * ChangedEmailMode - Handles email change verification display and logic.
 * 
 * This component manages the email change verification process, including:
 * - Token verification from URL parameters
 * - Success/error state handling
 * - Auto-logout on successful verification
 * - Auto-redirect on error
 * 
 * Uses StatusPage for consistent design.
 * Displays appropriate success or error messages based on verification result.
 * 
 * @example
 * <ChangedEmailMode />
 */
const ChangedEmailMode: React.FC = () => {
  const navigate = useNavigate()
  const { logout } = useAuth0()
  const { t } = useTranslation(['email_verified', 'common'])

  const params = useMemo(() => new URLSearchParams(window.location.search), [])

  const verifyChengedEmail = useVerifyEmail()

  const cardString = useMemo(() => {
    if (verifyChengedEmail.isSuccess) {
      return { title: t('change_email_success_title'), text: t('change_email_success_text') }
    } else {
      return { title: t('change_email_failed_title'), text: t('change_email_failed_text') }
    }
  }, [t, verifyChengedEmail.isSuccess])

  const handleVerifyChangedEmail = useCallback(() => {
    const token = params.get('token') ?? ''
    verifyChengedEmail.mutate(
      { token },
      {
        onSuccess: () => setTimeout(() => logout(), 3000),
        onError: () => setTimeout(() => navigate(Path.INDEX), 3000)
      }
    )
  }, [logout, navigate, params, verifyChengedEmail])

  useEffect(() => {
    handleVerifyChangedEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <StatusPage
      titleTrans={{ i18nKey: t(cardString?.title) }}
      descriptionTrans={{ i18nKey: t(cardString?.text) }}
      primaryButton={{
        text: t('common:continue'),
        onClick: () => navigate(Path.INDEX)
      }}
    />
  )
}

export default ChangedEmailMode
